import {
  HANDOFF_FIELDS,
  HANDOFF_LIMITS,
  type HandoffField,
  type HandoffSettings,
  type WhatsappSettings,
} from '@/services/whatsappSettingsService';

/**
 * Form logic of the „Übergabe an Mitarbeiter“ card, kept pure for tests.
 * Standard texts mirror contract livecenter §6 — the backend sends exactly
 * these when `busy_message` is null.
 */

export type Salutation = 'du' | 'sie';

export const HANDOFF_BUTTON_LABELS = ['Ja, bitte melden', 'Nein, danke'] as const;

export function defaultBusyMessage(salutation: Salutation): string {
  const you = salutation === 'sie' ? 'Sie' : 'dich';
  const withYou = salutation === 'sie' ? 'Ihnen' : 'dir';
  return (
    'Das tut mir leid – scheinbar sind gerade alle aus unserem Team beschäftigt. 🙏\n\n' +
    `Soll ich eine Rückmeldung für ${you} hinterlegen? Dann meldet sich jemand aus dem Team bei ${withYou}, sobald jemand frei ist.`
  );
}

export interface HandoffDraft {
  offerOnProblems: boolean;
  waitMinutes: string;
  idleMinutes: string;
  busyMessage: string;
}

export interface HandoffErrors {
  waitMinutes?: string;
  idleMinutes?: string;
  busyMessage?: string;
}

export function draftFrom(settings: HandoffSettings): HandoffDraft {
  return {
    offerOnProblems: settings.offer_on_problems,
    waitMinutes: String(settings.wait_minutes),
    idleMinutes: String(settings.idle_minutes),
    busyMessage: settings.busy_message ?? '',
  };
}

function minutes(value: string, range: { min: number; max: number }): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const n = Number(value.trim());
  return n >= range.min && n <= range.max ? n : null;
}

export function validateDraft(draft: HandoffDraft): HandoffErrors {
  const errors: HandoffErrors = {};
  const { waitMinutes: wait, idleMinutes: idle } = HANDOFF_LIMITS;
  if (minutes(draft.waitMinutes, wait) === null) errors.waitMinutes = `Bitte ${wait.min}–${wait.max} Minuten angeben.`;
  if (minutes(draft.idleMinutes, idle) === null) errors.idleMinutes = `Bitte ${idle.min}–${idle.max} Minuten angeben.`;
  if (draft.busyMessage.trim().length > HANDOFF_LIMITS.busyMessageMax) {
    errors.busyMessage = `Höchstens ${HANDOFF_LIMITS.busyMessageMax} Zeichen.`;
  }
  return errors;
}

/** The block the (valid) draft stands for; `enabled` from the saved state. */
export function settingsFromDraft(draft: HandoffDraft, enabled: boolean): HandoffSettings {
  const text = draft.busyMessage.trim();
  return {
    enabled,
    offer_on_problems: draft.offerOnProblems,
    wait_minutes: Number(draft.waitMinutes.trim()),
    idle_minutes: Number(draft.idleMinutes.trim()),
    busy_message: text ? text : null,
  };
}

/**
 * Only the fields the draft changed. What is not sent stays as it is — for an
 * agent number that means it keeps following the company's value.
 */
export function changedHandoffFields(draft: HandoffDraft, saved: HandoffSettings): Partial<HandoffSettings> {
  const next = settingsFromDraft(draft, saved.enabled);
  const changes: Partial<HandoffSettings> = {};
  for (const field of HANDOFF_FIELDS) {
    if ((next[field] ?? null) !== (saved[field] ?? null)) (changes as Record<string, unknown>)[field] = next[field];
  }
  return changes;
}

export function isDraftDirty(draft: HandoffDraft, saved: HandoffSettings): boolean {
  const clean = draftFrom(saved);
  return (
    draft.offerOnProblems !== clean.offerOnProblems ||
    draft.waitMinutes.trim() !== clean.waitMinutes ||
    draft.idleMinutes.trim() !== clean.idleMinutes ||
    draft.busyMessage.trim() !== clean.busyMessage.trim()
  );
}

/** Stepper: one minute up/down, clamped; an unparsable field starts from the bound. */
export function stepMinutes(value: string, delta: number, range: { min: number; max: number }): string {
  const n = /^\d+$/.test(value.trim()) ? Number(value.trim()) : delta > 0 ? range.min - 1 : range.max + 1;
  return String(Math.min(range.max, Math.max(range.min, n + delta)));
}

export type HandoffInheritance = Record<HandoffField, boolean>;

/** Short names of the fields for the "Eigene Werte für …" hint. */
export const HANDOFF_FIELD_NAMES: Record<HandoffField, string> = {
  enabled: 'Übergabe an/aus',
  offer_on_problems: 'Bei Problemen anbieten',
  wait_minutes: 'Wartezeit',
  idle_minutes: 'Rückgabe bei Ruhe',
  busy_message: 'Nachricht bei „niemand frei“',
};

/**
 * Per handoff field: does this agent number use the company's value? The
 * backend reports it flat by field name (contract livecenter §5); a field it
 * does not report is not claimed as inherited.
 */
export function handoffInheritance(inherited: WhatsappSettings['inherited']): HandoffInheritance {
  const flags = {} as HandoffInheritance;
  for (const field of HANDOFF_FIELDS) flags[field] = inherited?.[field] === true;
  return flags;
}

/** Fields this agent number sets itself, in card order. */
export function overriddenFields(inheritance: HandoffInheritance): HandoffField[] {
  return HANDOFF_FIELDS.filter((field) => !inheritance[field]);
}
